import { contacts } from '@/data/contacts';
import NowPlaying from '@/features/spotify/NowPlaying';
import PageMeta from '@/shared/components/PageMeta';
import { getSpaceBlogPosts } from '@/lib/content';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import ExternalLink from '@/shared/ui/external-link';
import { ArrowDown, ArrowRight, Camera, Github, Linkedin, Mail, Music, PenTool } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

const socialLinks = [
  { label: 'GitHub', href: contacts.github, icon: Github },
  { label: 'LinkedIn', href: contacts.linkedin, icon: Linkedin },
  { label: 'Email', href: contacts.emailHref, icon: Mail }
];

export default function SpacePage() {
  const { language, t } = useLanguage();
  const latestPost = useMemo(() => getSpaceBlogPosts(language)[0], [language]);
  const zh = language === 'zh';

  return (
    <div>
      <PageMeta title="Space" description="The personal side of zkb.bio — blog, photography, and what I'm listening to." path="/space" />
      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-muted/20" />
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />

        <div className="relative z-10 text-center max-w-3xl mx-auto px-6">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-br from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
            zkb
          </h1>

          <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
            {zh ? '这里是 zkb.bio 的另一面。' : 'The other side of zkb.bio.'}
          </p>

          <p className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed">
            {zh
              ? '白天我在 UCL 读历史、政治与经济学；其余时间写代码、造无人机、拍照片。这个空间放不进简历的那部分我：博客、摄影，和正在听的音乐。'
              : 'By day I read History, Politics and Economics at UCL; the rest of the time I write code, build UAVs and take photos. This space holds the parts of me that don’t fit on a CV: blog, photography, and whatever I’m listening to.'}
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <Button asChild size="lg" className="group">
              <Link to="/space/blog">
                <PenTool className="w-4 h-4 mr-2 group-hover:rotate-12 transition-transform" />
                {zh ? '阅读博客' : 'Read the blog'}
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="group">
              <Link to="/space/portfolio">
                <Camera className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                {zh ? '看看照片' : 'See the photos'}
              </Link>
            </Button>
          </div>

          <div className="flex justify-center gap-2">
            {socialLinks.map(link => (
              <Button key={link.label} variant="ghost" size="icon" asChild className="h-9 w-9">
                <ExternalLink href={link.href} aria-label={link.label}>
                  <link.icon className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" />
                </ExternalLink>
              </Button>
            ))}
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <ArrowDown className="w-5 h-5 text-muted-foreground/60" />
        </div>
      </section>

      {/* 最新内容 */}
      <section className="py-20 bg-muted/20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold mb-3">{zh ? '这里有什么' : 'What lives here'}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* 博客 */}
            <Card className="group hover:shadow-lg transition-all duration-300 border-0 bg-card/50 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <PenTool className="w-5 h-5 text-blue-500" />
                  </div>
                  <h3 className="font-semibold">{zh ? '博客' : 'Blog'}</h3>
                </div>
                {latestPost ? (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-foreground mb-1">{latestPost.meta.title}</p>
                    <p className="text-sm text-muted-foreground line-clamp-3">{latestPost.meta.summary}</p>
                  </div>
                ) : (
                  <p className="text-muted-foreground mb-4">{t('blogTagline')}</p>
                )}
                <Button asChild variant="ghost" className="group p-0 h-auto">
                  <Link to="/space/blog" className="text-primary group-hover:underline">
                    {zh ? '阅读更多' : 'Read more'}
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* 摄影 */}
            <Card className="group hover:shadow-lg transition-all duration-300 border-0 bg-card/50 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <Camera className="w-5 h-5 text-green-500" />
                  </div>
                  <h3 className="font-semibold">{zh ? '摄影' : 'Photography'}</h3>
                </div>
                <p className="text-muted-foreground mb-4">{t('portfolioTagline')}</p>
                <Button asChild variant="ghost" className="group p-0 h-auto">
                  <Link to="/space/portfolio" className="text-primary group-hover:underline">
                    {zh ? '查看作品' : 'View photos'}
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* 音乐 — Phase 5 接入 Spotify NowPlaying */}
            <Card className="group hover:shadow-lg transition-all duration-300 border-0 bg-card/50 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Music className="w-5 h-5 text-purple-500" />
                  </div>
                  <h3 className="font-semibold">{zh ? '正在听' : 'Now playing'}</h3>
                </div>
                <NowPlaying />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
